import { Button, ButtonColors } from '@chiroup/components/Button';
import React from 'react';
import { Regions } from '../../../../../constants';
import ROM from './ROM';

type Props = {
  rows?: any[];
  onChange: (
    tab: string,
    key: string,
    val: {
      value?: number;
      pain?: boolean;
      side?: 'Right' | 'Left' | 'Both' | 'SpinalArea';
    }
  ) => void;
  onChangeComplete?: (val: any) => void;
  currentTab: string;
  value: {
    [key: string]: {
      [key: string]: {
        [key: string]: { value?: number; pain?: boolean };
      };
    };
  };
  romSliderStep: number;
  signed?: boolean;
};

const UnifiedRangeOfMotionRegion: React.FC<Props> = ({
  rows = [],
  onChange,
  onChangeComplete,
  currentTab,
  value,
  romSliderStep,
  signed = false,
}) => {
  const spinalRegion =
    currentTab === Regions.C || currentTab === Regions.L || currentTab === Regions.T;

  const updateDetail = (
    tab: string,
    key: string,
    val: {
      value?: number;
      pain?: boolean;
      side?: 'Right' | 'Left' | 'Both' | 'SpinalArea';
    }
  ) => {
    onChange(tab, key, val);
  };

  const allNormal = () => {
    // Build complete update object to avoid multiple state updates
    const currentValues = value?.[currentTab] || {};
    const updatedValues = { ...currentValues };

    rows.forEach(rom => {
      if (spinalRegion) {
        updatedValues[rom.value] = {
          ...updatedValues[rom.value],
          SpinalArea: {
            value: rom.normal,
            pain: false,
          },
        };
      } else {
        updatedValues[rom.value] = {
          ...updatedValues[rom.value],
          Left: {
            value: rom.normal,
            pain: false,
          },
          Right: {
            value: rom.normal,
            pain: false,
          },
        };
      }
    });

    // Single update with all changes
    const newValue = {
      ...value,
      [currentTab]: {
        ...updatedValues,
        touched: true, // Set touched when user interacts with this region (like legacy)
      },
    };

    // Use onChangeComplete if available for bulk updates
    if (onChangeComplete) {
      onChangeComplete(newValue);
    } else {
      // Fallback to individual updates
      rows.forEach(rom => {
        if (spinalRegion) {
          updateDetail(currentTab, rom.value, {
            value: rom.normal,
            pain: false,
            side: 'SpinalArea',
          });
        } else {
          updateDetail(currentTab, rom.value, {
            value: rom.normal,
            pain: false,
            side: 'Left',
          });
          updateDetail(currentTab, rom.value, {
            value: rom.normal,
            pain: false,
            side: 'Right',
          });
        }
      });
    }
  };

  const clearAll = () => {
    if (onChangeComplete) {
      // Build complete update object for bulk clear
      const currentValues = value?.[currentTab] || {};
      const updatedValues = { ...currentValues };

      rows.forEach(rom => {
        if (spinalRegion) {
          updatedValues[rom.value] = {
            ...updatedValues[rom.value],
            SpinalArea: {
              value: undefined,
              pain: false,
            },
          };
        } else {
          updatedValues[rom.value] = {
            ...updatedValues[rom.value],
            Left: {
              value: undefined,
              pain: false,
            },
            Right: {
              value: undefined,
              pain: false,
            },
          };
        }
      });

      const newValue = {
        ...value,
        [currentTab]: {
          ...updatedValues,
          touched: true,
        },
      };

      onChangeComplete(newValue);
    } else {
      // Fallback to individual updates
      rows.forEach(rom => {
        if (spinalRegion) {
          updateDetail(currentTab, rom.value, {
            value: undefined,
            pain: false,
            side: 'SpinalArea',
          });
        } else {
          updateDetail(currentTab, rom.value, {
            value: undefined,
            pain: false,
            side: 'Left',
          });
          updateDetail(currentTab, rom.value, {
            value: undefined,
            pain: false,
            side: 'Right',
          });
        }
      });
    }
  };

  const getValue = ({
    currentValue,
    percent,
    normal,
    max,
    min,
  }: {
    currentValue: number;
    percent: number;
    normal: number;
    max: number;
    min: number;
  }) => {
    let changeAmount;
    let rawValue;

    if (normal !== 0) {
      changeAmount = normal * (percent / 100);
      rawValue = (currentValue ?? normal) + changeAmount;
    } else {
      const positiveDistance = Math.abs(max - normal);
      const negativeDistance = Math.abs(min - normal);
      const symmetricDistance = Math.max(positiveDistance, negativeDistance);
      changeAmount = symmetricDistance * (percent / 100);
      rawValue = (currentValue ?? 0) + changeAmount;
    }

    // Snap to romSliderStep and ensure movement in the requested direction
    const direction = percent >= 0 ? 1 : -1;
    let nextValue =
      Math[direction > 0 ? 'floor' : 'ceil'](rawValue / romSliderStep) * romSliderStep;

    if (typeof currentValue === 'number' && currentValue === nextValue) {
      nextValue += direction * romSliderStep;
    }

    return Math.max(min, Math.min(max, nextValue));
  };

  const percentChange = (percent: number) => {
    if (onChangeComplete) {
      const currentValues = value?.[currentTab] || {};
      const updatedValues = { ...currentValues };

      rows.forEach(rom => {
        if (spinalRegion) {
          const currentValue = value?.[currentTab]?.[rom.value]?.SpinalArea?.value ?? rom.normal;
          const newValue = getValue({
            currentValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          updatedValues[rom.value] = {
            ...updatedValues[rom.value],
            SpinalArea: {
              value: newValue,
              pain: value?.[currentTab]?.[rom.value]?.SpinalArea?.pain || false,
            },
          };
        } else {
          const currentLeftValue = value?.[currentTab]?.[rom.value]?.Left?.value ?? rom.normal;
          const currentRightValue = value?.[currentTab]?.[rom.value]?.Right?.value ?? rom.normal;

          const newLeftValue = getValue({
            currentValue: currentLeftValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          const newRightValue = getValue({
            currentValue: currentRightValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          updatedValues[rom.value] = {
            ...updatedValues[rom.value],
            Left: {
              value: newLeftValue,
              pain: value?.[currentTab]?.[rom.value]?.Left?.pain || false,
            },
            Right: {
              value: newRightValue,
              pain: value?.[currentTab]?.[rom.value]?.Right?.pain || false,
            },
          };
        }
      });

      const newValue = {
        ...value,
        [currentTab]: {
          ...updatedValues,
          touched: true, // Set touched when user interacts with this region (like legacy)
        },
      };

      onChangeComplete(newValue);
    } else {
      // Fallback to individual updates
      rows.forEach(rom => {
        if (spinalRegion) {
          const currentValue = value?.[currentTab]?.[rom.value]?.SpinalArea?.value ?? rom.normal;
          const newValue = getValue({
            currentValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          updateDetail(currentTab, rom.value, {
            value: newValue,
            pain: value?.[currentTab]?.[rom.value]?.SpinalArea?.pain || false,
            side: 'SpinalArea',
          });
        } else {
          const currentLeftValue = value?.[currentTab]?.[rom.value]?.Left?.value ?? rom.normal;
          const currentRightValue = value?.[currentTab]?.[rom.value]?.Right?.value ?? rom.normal;

          const newLeftValue = getValue({
            currentValue: currentLeftValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          const newRightValue = getValue({
            currentValue: currentRightValue,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          updateDetail(currentTab, rom.value, {
            value: newLeftValue,
            pain: value?.[currentTab]?.[rom.value]?.Left?.pain || false,
            side: 'Left',
          });

          updateDetail(currentTab, rom.value, {
            value: newRightValue,
            pain: value?.[currentTab]?.[rom.value]?.Right?.pain || false,
            side: 'Right',
          });
        }
      });
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 justify-between my-4">
        <div>
          <Button
            text="All normal"
            color={ButtonColors.plainWithBorder}
            onClick={allNormal}
            className="mr-2"
            disabled={signed}
          />
          {!!value?.[currentTab] && (
            <Button
              text="Clear all"
              color={ButtonColors.plainWithBorder}
              onClick={clearAll}
              disabled={signed}
            />
          )}
        </div>
        <div className="ml-2">
          <Button
            text="-25%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(-25)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="-5%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(-5)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="+5%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(5)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="+25%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(25)}
            disabled={signed}
          />
        </div>
      </div>
      {!spinalRegion ? (
        <div className="flex flex-col 2xl:flex-row gap-4 min-w-0">
          <ROM
            rows={rows}
            value={value}
            onChange={updateDetail}
            onChangeComplete={onChangeComplete}
            currentTab={currentTab}
            side="Left"
            showSide={true}
            romSliderStep={romSliderStep}
            disabled={signed}
          />
          <ROM
            rows={rows}
            value={value}
            onChange={updateDetail}
            onChangeComplete={onChangeComplete}
            currentTab={currentTab}
            side="Right"
            showSide={true}
            romSliderStep={romSliderStep}
            disabled={signed}
          />
        </div>
      ) : (
        <ROM
          rows={rows}
          value={value}
          onChange={updateDetail}
          onChangeComplete={onChangeComplete}
          currentTab={currentTab}
          side="SpinalArea"
          romSliderStep={romSliderStep}
          disabled={signed}
        />
      )}
    </>
  );
};

export default UnifiedRangeOfMotionRegion;
